import React, { useState, useEffect, useRef } from 'react';
import * as R from 'ramda';
import { Controller } from 'react-hook-form';
import {
  TextField,
  Divider,
  InputLabel,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Typography,
  Tooltip,
} from '@material-ui/core';
import Delete from '@material-ui/icons/Delete';
import { useTranslation } from 'react-i18next';
import { ChangeOrderButton } from '@views/cases/components/ChangeOrderButton';
import { OBJ, getUrnoidToPropFunc } from '@utils/data-utils';

const emailUrnoid = 'urn:oid:0.9.2342.19200300.100.1.3';

export const Reviewer = ({
  name,
  existingEmail,
  index = 0,
  classes,
  move,
  remove,
  totalCount,
  errors = OBJ,
  setValue,
  getValues,
  repeatedEmailErrors,
  disableArrows = { up: false, down: false },
  control,
}) => {
  const { t } = useTranslation();
  const isEmailError = R.has('email', errors);
  const emailName = `${name}.email`;
  const emailInputRef = useRef(null);
  const [reviewer, setReviewer] = useState(null);

  const updateReviewer = () => {
    const values = getValues();
    if (values && values.reviewers && values.reviewers.length > index)
      setReviewer({ ...values.reviewers[index] });
  };

  const setEmailValue = (val) => {
    setValue(`${name}.email`, val);
    updateReviewer();
  };

  useEffect(() => {
    setEmailValue(existingEmail);
  }, [existingEmail]);

  const { validation: emailValidation } = getUrnoidToPropFunc(emailUrnoid)(t);
  const checkKeyDown = (e) => {
    if (e.key === 'Enter') e.preventDefault();
  };

  useEffect(() => {
    if (repeatedEmailErrors && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [repeatedEmailErrors]);

  return (
    <Card className={classes.box}>
      <CardContent>
        <Typography variant="subtitle1" component="h2" className={classes.uppercase}>
          {t('Reviewer', { index: totalCount < 2 ? undefined : index + 1 })}
        </Typography>
        <Typography variant="body2" className={classes.subtitle}>
          {t('Reviewer details')}
        </Typography>
        <Divider />
        <InputLabel htmlFor={`reviewer-${index}-email`} className={classes.subtitle}>
          {t('EmailOptional')}
        </InputLabel>
        <Controller
          name={emailName}
          defaultValue={reviewer?.email}
          control={control}
          rules={{
            validate: {
              // Only validate format if email is provided
              validEmail: (value) => {
                if (!value || value.trim() === '') return true; // Optional: allow empty
                return emailValidation.pattern.value.test(value) || emailValidation.pattern.message;
              },
            },
          }}
          render={({ value, ref, onChange }) => (
            <TextField
              value={value}
              id={`reviewer-${index}-email`}
              inputRef={(node) => {
                emailInputRef.current = node;
                if (typeof ref === 'function') {
                  ref(node);
                } else if (ref && typeof ref === 'object' && 'current' in ref) {
                  // eslint-disable-next-line no-param-reassign
                  ref.current = node;
                }
              }}
              fullWidth
              helperText={
                isEmailError || repeatedEmailErrors
                  ? null
                  : t('Please make sure you type the correct address')
              }
              error={isEmailError || repeatedEmailErrors}
              onChange={(e) => {
                const val = e.target.value.trim();
                onChange(val);
              }}
              inputProps={{
                maxLength: 254,
                'aria-describedby': isEmailError
                  ? 'reviewer-email-error'
                  : repeatedEmailErrors
                  ? 'reviewer-duplicate-email-error'
                  : undefined,
              }}
              onKeyDown={(e) => checkKeyDown(e)}
            />
          )}
        />
        {isEmailError && (
          <Typography id="reviewer-email-error" color="error" role="alert" aria-live="assertive">
            {errors.email.message}
          </Typography>
        )}
        {repeatedEmailErrors && (
          <Typography
            id="reviewer-duplicate-email-error"
            color="error"
            role="alert"
            aria-live="assertive"
          >
            {repeatedEmailErrors}
          </Typography>
        )}
      </CardContent>
      <CardActions>
        {index !== totalCount - 1 && !disableArrows.down && (
          <ChangeOrderButton onClick={() => move(index, index + 1)} ariaLabel={t('Move down')} />
        )}
        {index !== 0 && !disableArrows.up && (
          <ChangeOrderButton
            direction="up"
            onClick={() => move(index, index - 1)}
            ariaLabel={t('Move Up')}
          />
        )}
        <Tooltip title={t('Delete')}>
          <IconButton onClick={() => remove(index)} aria-label={t('Delete')}>
            <Delete />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
};
